/**
 * Encounter Check Utilities
 * 
 * Determines if monster encounters occur during travel based on terrain type
 * and player's monster frequency setting
 */

import { TerrainType } from '../types/terrain';
import { getMonsterProbabilitySetting } from '../components/SettingsPanel';

/**
 * Check if a monster encounter should occur
 * Uses player's monster frequency setting directly as the encounter rate
 * 
 * @param terrain - Terrain type of the current hex
 * @returns true if encounter occurs, false otherwise
 */
export function checkMonsterEncounter(terrain: TerrainType): boolean {
  // Never encounter monsters in settlements
  if (shouldSkipEncounter(terrain)) {
    return false;
  }
  
  // Get player's monster frequency setting (0 = no monsters, 1 = always)
  // This IS the encounter rate (e.g., 0.083 = 8.3% chance per hex)
  const encounterRate = getMonsterProbabilitySetting();
  
  // If player has disabled monsters (frequency = 0), never encounter
  if (encounterRate === 0) {
    console.log('🚫 Travel encounter disabled by player (encounterRate = 0)');
    return false;
  }
  
  // Use the setting directly as the encounter rate
  const roll = Math.random();
  const hasEncounter = roll < encounterRate;
  
  console.log(`🎲 Travel encounter check: rolled ${roll.toFixed(3)}, threshold ${encounterRate.toFixed(3)} (${(encounterRate * 100).toFixed(1)}%), result: ${hasEncounter ? 'ENCOUNTER' : 'no encounter'}`);
  
  return hasEncounter;
}

/**
 * Check if encounter should be skipped for this terrain type
 * Settlements never have monster encounters
 * 
 * @param terrain - Terrain type to check
 * @returns true if encounters should be skipped
 */
export function shouldSkipEncounter(terrain: TerrainType): boolean {
  return terrain === TerrainType.Settlement;
}

/**
 * Get encounter message for debugging/logging
 */
export function getEncounterMessage(terrain: TerrainType, hasEncounter: boolean): string {
  if (shouldSkipEncounter(terrain)) {
    return 'No encounter (Settlement)';
  }
  return hasEncounter ? 'Monster encounter!' : 'No encounter';
}

